// This is an interface between the editor and the graph library, in order
// to support switching the graph engine between various libraries.
import cytoscape from 'cytoscape';
import cssVars from 'cytoscape-css-variables';
import { defaultTheme, highContrastTheme } from '../../../../src/themes';
import Utils from './utils';

cytoscape.use(cssVars);

window.$empty = function () {};
export default class Graph {
  constructor(element, opts) {
    this.theme = Utils.getUserTheme() === 'high_contrast' ? highContrastTheme : defaultTheme;
    this.element = element;
    this.options = $.extend(
      {},
      {
        onNodeAdded: $empty,
        onEdgeAdded: $empty,
        onNodeMoved: $empty,
        onNodeRemoved: $empty,
        onEdgeRemoved: $empty,
        edgeParams: $empty,
        onBehaviorClicked: $empty,
        onEdgeToolClicked: $empty,
        onNodeRequested: $empty,
        locked: false,
        ungrabbable: false,
        zoom: 1,
        userZoomingEnabled: false,
        userPanningEnabled: false,
        maxZoom: 1.5,
        iconPaths: {
          edgeHandle: '/assets/images/editor/edgehandle.svg',
          zoomNode: '/assets/images/editor/zoom_node.svg',
          editNode: '/assets/images/editor/edit_node.svg',
          addNode: '/assets/images/editor/add_node.svg',
          deleteEdge: '/assets/images/editor/edge-delete.svg',
          hiddenEdge: '/assets/images/icons/glyphicons/glyphicons-halflings/svg/glyphicons-53-eye-close.svg',
          codeEdge: '/assets/images/icons/glyphicons/glyphicons-halflings/svg/glyphicons-45-keys.svg',
          lockedEdge: '/assets/images/icons/glyphicons/glyphicons-halflings/svg/glyphicons-204-lock.svg',
          toolsIcon: '/assets/images/theme-2/icons/graph/gear_graph.png',
          visibleEdge: '/assets/images/icons/glyphicons/glyphicons-halflings/svg/glyphicons-52-eye-open.svg',
        },
      },
      opts,
    );

    const iconKeys = Object.keys(this.options.iconPaths);
    for (var i = 0; i < iconKeys.length; i++) {
      let img = new Image();
      img.className = 'svg';
      img.src = this.options.iconPaths[iconKeys[i]];
      this.options.iconPaths[iconKeys[i]] = img;
    }
  }

  /**
		Add a node at the provided position. The position is assumed to be a "rendered" position, that is - already converted to 
		the proper coordinate system.

		@param {Object} position An object with the keys x an y. 
		@param {Object} opts A set of options - merged into the set of data passed to the graph when adding the node.
	
		@return {CY} A cytoscape element that corresponds to the added node
	*/
  addNode(position, opts) {
    let data = $.extend(
      true,
      {
        group: 'nodes',
      },
      opts,
    );
    if (opts.truePosition) {
      data.position = position;
    } else {
      data.renderedPosition = position;
    }
    return this.cy.add(data);
  }

  /**
		Add an edge between the provided source and target. 
	
		@param {String} source The id of the source node
		@param {String} target The id of the target node
		@param {Object} opts A set of options - merged into the set of data passed to the graph when adding the edge.
		@return {CY} A cytoscape element that corresponds to the added edge
	*/
  addEdge(source, target, opts) {
    return this.cy.add(
      $.extend(
        true,
        {
          group: 'edges',
          data: {
            source: source,
            target: target,
          },
        },
        opts,
      ),
    );
  }

  fetch(id) {
    return this.cy.$(id);
  }

  removeNode(element) {
    this.cy.remove(element);
    this.removeHandle();
  }

  removeEdge(element) {
    this.cy.remove(element);
  }

  getVar(variable) {
    return this.cssVariables ? this.cssVariables.getVar(variable) : 'transparent';
  }

  build(el) {
    this.cy = cytoscape({
      container: this.element,
      layout: {
        name: 'preset',
        fit: false,
      },
      autolock: this.options.locked,
      autoungrabify: this.options.ungrabbable,
      wheelSensitivity: 0.4,
      zoom: this.options.zoom,
      minZoom: this.options.minZoom,
      maxZoom: this.options.maxZoom,
      userZoomingEnabled: this.options.userZoomingEnabled,
      userPanningEnabled: this.options.userPanningEnabled,
      elements: el.map((element) => {
        if (element.data && element.data.name === null) {
          delete element.data.name;
        }
        return element;
      }),
      style: [
        {
          selector: 'core',
          style: {
            'active-bg-opacity': 0,
            'selection-box-opacity': 0.5,
          },
        },
        {
          selector: 'node[group != "indicators"]',
          style: {
            width: '120',
            height: '120',
            'background-color': () => this.getVar('--menuButtonBorder'),
            'border-opacity': 0,
            'background-image': function (el) {
              if (el.data('image')) {
                return el.data('image');
              } else {
                return 'none';
              }
            },
            'background-width': 'auto',
            'background-height': 'auto',
            'background-fit': 'cover',
            'z-index': 3,
            'z-index-compare': 'manual',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: 'node[!initialized]',
          style: {
            width: '120',
            height: '120',
            'background-color': () => this.getVar('--black'),
            'background-image-opacity': 0.5,
            'background-image': '/assets/images/tmp/v2/icons/uninitialized-node.png',
            'background-fit': 'none',
            'border-color': () => this.getVar('--white'),
            'border-width': '3px',
            'border-opacity': 0.5,
            'transition-property': 'border-opacity, background-image-opacity',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.eh-presumptive-target',
          style: {
            'border-width': 6,
            'border-color': () => this.getVar('--loopsGreen'),
            'border-opacity': 0.5,
          },
        },
        {
          selector: 'node[!type].hover',
          style: {
            width: '124',
            height: '124',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.eh-handle',
          style: {
            width: 24,
            height: 24,
            visibility: 'visible',
            'z-index': 10,
            'border-width': 0,
            'background-image': '/assets/images/icons/plus-green.svg',
            'background-image-opacity': 0.8,
            'overlay-opacity': 0,
            'background-width': 'auto',
            'background-height': 'auto',
            'background-fit': 'cover',
            'transition-property': 'height, width, opacity',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.eh-handle.hover, .eh-handle:active',
          style: {
            width: 26,
            height: 26,
            'background-image-opacity': 1,
            'transition-property': 'height, width, opacity',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.eh-handle.disabled, .eh-handle.disabled.hover, .eh-handle.disabled:active',
          style: {
            visibility: 'hidden',
          },
        },
        {
          selector: '.behavior',
          style: {
            width: '37',
            height: '37',
            'z-index': 2,
            'background-fit': 'none',
            'border-width': '2px',
            'background-width': '100%',
            'background-height': '100%',
            'border-opacity': 1,
            'background-image-opacity': 1,
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: 'node[group = "indicators"][type = "state"]',
          style: {
            width: '12',
            height: '12',
            'z-index': 3,
            'background-fit': 'cover',
            'background-image': '/assets/images/theme-2/icons/states/accepted.svg',
          },
        },
        {
          selector: 'node[group = "indicators"][type = "notification"]',
          style: {
            width: '12',
            height: '12',
            'z-index': 3,
            'background-color': () => this.getVar('--behaviorPink'),
          },
        },
        {
          selector: 'node[group = "indicators"][type = "announceNode"]',
          style: {
            width: '12',
            height: '12',
            'z-index': 4,
            'background-color': () => this.getVar('--behaviorYellow'),
            'border-width': '1px',
            'border-color': () => this.getVar('--darkHorizonBackground'),
          },
        },
        {
          selector: 'node[group = "indicators"][type = "announceHub"]',
          style: {
            width: '16',
            height: '16',
            'z-index': 3,
            'background-color': () => this.getVar('--behaviorYellow'),
            'border-width': '1px',
            'border-color': () => this.getVar('--darkHorizonBackground'),
          },
        },
        {
          selector: '.chunk',
          style: {
            width: '37',
            height: '37',
            'border-width': '2px',
            'border-color': () => this.getVar('--behaviorYellow'),
            'background-width': '100%',
            'background-height': '100%',
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_06.png',
            'border-opacity': 1,
            'z-index': 2,
            'background-image-opacity': 1,
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.chunk.hover, .chunk.selected',
          style: {
            'background-color': () => this.getVar('--behaviorYellow'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_07.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.chunk.checkmark',
          style: {
            'background-width': '45%',
            'background-height': '25%',
            'background-image': '/assets/images/theme-2/icons/graph/done/subcat.png',
          },
        },
        {
          selector: '.video',
          style: {
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_42.png',
          },
        },
        {
          selector: '.video.hover, .video.selected',
          style: {
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_44.png',
          },
        },
        {
          selector: '.video.checkmark',
          style: {
            'background-width': '35%',
            'background-height': '35%',
            'background-image': '/assets/images/theme-2/icons/graph/done/video.png',
          },
        },
        {
          selector: '.link',
          style: {
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_43.png',
          },
        },
        {
          selector: '.link.hover, .link.selected',
          style: {
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_45.png',
          },
        },
        {
          selector: '.link.checkmark',
          style: {
            'background-width': '40%',
            'background-height': '40%',
            'background-image': '/assets/images/theme-2/icons/graph/done/link.png',
          },
        },
        {
          selector: '.placeholder',
          style: {
            width: 37,
            height: 37,
            opacity: 1,
            'z-index': 2,
            'background-image': 'none',
            'background-color': () => this.getVar('--black'),
            'background-height': '10px',
            'background-width': '10px',
            'border-color': () => this.getVar('--white'),
            'border-opacity': 0.2,
            'border-width': '3px',
            'transition-property': 'border-opacity, background-image-opacity, width, height',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.placeholder-hover',
          style: {
            'background-image-opacity': 0.5,
            'background-image': '/assets/images/theme-2/icons/graph/plus.png',
            'background-height': '12px',
            'background-width': '12px',
            'border-opacity': 0.5,
            'transition-property': 'border-opacity, background-image-opacity, width, height',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.placeholder-new',
          style: {
            width: 37,
            height: 37,
            'background-image-opacity': 0.5,
            'background-image': '/assets/images/theme-2/icons/graph/plus.png',
            'background-height': '12px',
            'background-width': '12px',
            'border-opacity': 0.5,
            'transition-property': 'border-opacity, background-image-opacity, width, height',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.placeholder-new-hover',
          style: {
            'border-opacity': 0.7,
            'background-image-opacity': 0.7,
            'transition-property': 'border-opacity, background-image-opacity, width, height',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.highlight',
          style: {
            'border-color': () => this.getVar('--notification'),
            'background-color': () => this.getVar('--notification'),
          },
        },
        {
          selector: '.quiz',
          style: {
            'border-color': () => this.getVar('--behaviorPink'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_10.png',
          },
        },
        {
          selector: '.quiz.hover, .quiz.selected',
          style: {
            'background-color': () => this.getVar('--behaviorPink'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_11.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.quiz.checkmark',
          style: {
            'background-width': '50%',
            'background-height': '15%',
            'background-image': '/assets/images/theme-2/icons/graph/done/quiz.png',
          },
        },
        {
          selector: '.task',
          style: {
            'border-color': () => this.getVar('--behaviorBlue'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_30.png',
          },
        },
        {
          selector: '.task.hover, .task.selected',
          style: {
            'background-color': () => this.getVar('--behaviorBlue'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_31.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.task.checkmark',
          style: {
            'background-width': '25%',
            'background-height': '25%',
            'background-image': '/assets/images/theme-2/icons/graph/done/default.png',
          },
        },
        {
          selector: '.group',
          style: {
            'border-color': () => this.getVar('--behaviorGreen'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_22.png',
          },
        },
        {
          selector: '.group.hover, .group.selected',
          style: {
            'background-color': () => this.getVar('--behaviorGreen'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_23.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.group.checkmark',
          style: {
            'background-width': '40%',
            'background-height': '40%',
            'background-image': '/assets/images/theme-2/icons/graph/done/connected.png',
          },
        },
        {
          selector: '.public_blog',
          style: {
            'border-color': () => this.getVar('--behaviorGreen'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_26.png',
          },
        },
        {
          selector: '.public_blog.hover, .public_blog.selected',
          style: {
            'background-color': () => this.getVar('--behaviorGreen'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_27.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.public_blog.checkmark',
          style: {
            'background-width': '40%',
            'background-height': '40%',
            'background-image': '/assets/images/theme-2/icons/graph/done/noticeboard.png',
          },
        },
        {
          selector: '.private_blog',
          style: {
            'border-color': () => this.getVar('--behaviorBlue'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_34.png',
          },
        },
        {
          selector: '.private_blog.hover, .private_blog.selected',
          style: {
            'background-color': () => this.getVar('--behaviorBlue'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_35.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.private_blog.checkmark',
          style: {
            'background-width': '40%',
            'background-height': '40%',
            'background-image': '/assets/images/theme-2/icons/graph/done/logbook.png',
          },
        },
        {
          selector: '.written_quiz',
          style: {
            'border-color': () => this.getVar('--behaviorBlue'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_18.png',
          },
        },
        {
          selector: '.written_quiz.hover, .written_quiz.selected',
          style: {
            'background-color': () => this.getVar('--behaviorBlue'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_19.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.written_quiz.checkmark',
          style: {
            'background-width': '25%',
            'background-height': '35%',
            'background-image': '/assets/images/theme-2/icons/graph/done/question.png',
          },
        },
        {
          selector: '.legend',
          style: {
            'border-color': () => this.getVar('--behaviorYellow'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_01.png',
          },
        },
        {
          selector: '.legend.hover, .legend.selected',
          style: {
            'background-color': () => this.getVar('--behaviorYellow'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_02.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.legend.checkmark',
          style: {
            'background-width': '25%',
            'background-height': '25%',
            'background-image': '/assets/images/theme-2/icons/graph/done/concept.png',
          },
        },
        {
          selector: '.correlation',
          style: {
            'border-color': () => this.getVar('--behaviorPink'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_14.png',
          },
        },
        {
          selector: '.correlation.hover, .correlation.selected',
          style: {
            'background-color': () => this.getVar('--behaviorPink'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_15.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.correlation.checkmark',
          style: {
            'background-width': '45%',
            'background-height': '45%',
            'background-image': '/assets/images/theme-2/icons/graph/done/correlation.png',
          },
        },
        {
          selector: '.evaluation',
          style: {
            'border-color': () => this.getVar('--behaviorPurple'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_38.png',
          },
        },
        {
          selector: '.evaluation.hover, .evaluation.selected',
          style: {
            'background-color': () => this.getVar('--behaviorPurple'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_39.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.evaluation.checkmark',
          style: {
            'background-width': '30%',
            'background-height': '40%',
            'background-image': '/assets/images/theme-2/icons/graph/done/evaluation.png',
          },
        },
        {
          selector: '.checklist',
          style: {
            'border-color': () => this.getVar('--behaviorBlue'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_46.png',
          },
        },
        {
          selector: '.checklist.hover, .checklist.selected',
          style: {
            'background-color': () => this.getVar('--behaviorBlue'),
            'background-image': '/assets/images/theme-2/icons/graph/map-icons-2x_47.png',
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector: '.checklist.checkmark',
          style: {
            'background-width': '40%',
            'background-height': '40%',
            'background-image': '/assets/images/theme-2/icons/graph/done/checklist.png',
          },
        },

        {
          selector: '.checkmark',
          style: {
            'border-color': () => this.getVar('--menuButtonBorder'),
            'background-color': 'transparent',
            'text-opacity': 0.3,
          },
        },
        {
          selector: '.hub_completed',
          style: {
            'background-opacity': 0.3,
            'background-image-opacity': 0.3,
          },
        },
        {
          selector: 'edge',
          style: {
            width: '2px',
            'curve-style': 'bezier',
            'line-color': () => this.getVar('--defaultPlaceholder'),
          },
        },
        {
          selector: '[?optional]',
          style: {
            'line-style': 'dashed',
          },
        },
        {
          selector: '.invisible',
          style: {
            display: 'none',
          },
        },
        {
          selector: '.hidden, .setup',
          style: {
            display: 'none',
          },
        },
        {
          selector: '[behavior="hidden"]',
          style: {
            opacity: 0.8,
            display: 'element',
          },
        },
        {
          selector: '.teacherHidden',
          style: {
            opacity: 0.6,
          },
        },
        {
          selector: '.codeLocked, .codeMetaLocked, [metaLockedBy="code"], [lock_state="code"]',
          style: {
            'background-image': function (el) {
              if (el.data('image')) {
                return el.data('image') + ' /assets/images/theme-2/icons/graph/node-passwd-protected.png';
              } else {
                return ['/assets/images/theme-2/icons/graph/node-passwd-protected.png'];
              }
            },
          },
        },
        {
          selector: '[metaLockedBy="locked"], [lock_state="locked"]',
          style: {
            'background-image': function (el) {
              if (el.data('image')) {
                return el.data('image') + ' /assets/images/theme-2/icons/graph/node-locked.png';
              } else {
                return ['/assets/images/theme-2/icons/graph/node-locked.png'];
              }
            },
          },
        },
        {
          selector: '[?type][metaLockedBy]',
          style: {
            label: '',
          },
        },
        {
          selector: '.virtual',
          style: {
            visibility: 'hidden',
          },
        },
        {
          selector: 'edge.zoom_3',
          style: {
            width: 4,
          },
        },
        {
          selector: '.zoom_4[?group], .zoom_5[?group], .zoom_6[?group]',
          style: {
            'border-width': '3px',
          },
        },
        {
          selector: 'edge.zoom_4, edge.zoom_5',
          style: {
            width: 6,
          },
        },
        {
          selector: '.zoom_7[?group]',
          style: {
            width: 0,
          },
        },
        {
          selector: 'edge.zoom_6, edge.zoom_7',
          style: {
            width: 8,
          },
        },
        {
          selector: 'edge.eh-preview',
          style: {
            'line-color': () => this.getVar('--loopsGreen'),
            'line-style': 'dashed',
            width: 3,
          },
        },
        {
          selector: '.eh-ghost-edge',
          style: {
            'line-color': () => this.getVar('--loopsGreen'),
            'target-arrow-color': () => this.getVar('--loopsGreen'),
            'target-arrow-shape': 'circle',
            'arrow-scale': 2,
            'z-index-compare': 'manual',
            'z-index': 4,
            opacity: 0.8,
          },
        },

        // nodes
        {
          selector: 'node[name]',
          style: {
            label: 'data(name)',
            'font-family': 'Roboto',
            'font-size': '14',
            'text-wrap': 'wrap',
            color: () => this.getVar('--hoverGrey'),
            'text-valign': 'top',
            'text-halign': 'center',
            'transition-property': 'color, opacity',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
            'text-events': 'yes',
            'text-outline-color': () => this.getVar('--black'),
            'text-outline-width': 1,
            'text-outline-opacity': 1,
            'text-justification': 'center',
            'line-height': 1.4,
          },
        },
        {
          selector: 'node[name].readability',
          style: {
            'font-family': 'opendyslexic3regular',
          },
        },
        {
          selector: 'node.hover, node.selected, node.current-hub',
          style: {
            color: () => this.getVar('--white'),
          },
        },
        {
          selector: '.mask-1, .mask-2, .mask-128',
          style: {
            'text-valign': 'center',
            'text-halign': 'right',
            'text-margin-x': 8,
            'text-margin-y': 0,
          },
        },
        {
          selector: '.mask-8, .mask-16, .mask-32',
          style: {
            'text-valign': 'center',
            'text-halign': 'left',
            'text-margin-x': -8,
            'text-margin-y': 0,
          },
        },
        {
          selector: '.mask-4',
          style: {
            'text-valign': 'bottom',
            'text-halign': 'center',
            'text-margin-x': 0,
            'text-margin-y': 12,
          },
        },
        {
          selector: '.mask-64',
          style: {
            'text-valign': 'top',
            'text-halign': 'center',
            'text-margin-x': 0,
            'text-margin-y': -12,
          },
        },
        {
          selector: `node.zoom_6, node.zoom_7`,
          style: {
            'font-size': 0,
          },
        },

        // hubs
        {
          selector: 'node[!type]',
          style: {
            'text-valign': 'top',
            'text-halign': 'center',
            'text-margin-x': 0,
            'text-margin-y': -92,
            'font-size': '19',
            'font-weight': 'bold',
            'text-events': 'yes',
          },
        },
        {
          selector: 'node[!type].hover',
          style: {
            'text-margin-x': 0,
            'text-margin-y': -90,
          },
        },
        {
          selector: 'node[!type].zoom_6',
          style: {
            'font-size': '36',
            'text-margin-y': -72,
          },
        },
        {
          selector: 'node[!type].zoom_7',
          style: {
            'font-size': '42',
            'text-margin-y': -62,
          },
        },

        // start hub icon
        {
          selector: 'node[group = "indicators"][type = "startHubIcon"]',
          style: {
            width: '24',
            height: '24',
            'text-events': 'no',
            events: 'no',
            'background-image': '/assets/images/theme-2/icons/arrow.svg',
            'background-fit': 'cover',
            'border-width': 0,
            'background-image-opacity': 1,
            'transition-property': 'height, width',
            'transition-duration': '0.20s',
            'transition-timing-function': 'ease-in-out',
          },
        },
        {
          selector:
            'node[group = "indicators"][type = "startHubIcon"].zoom_4, node[group = "indicators"][type = "startHubIcon"].zoom_5',
          style: {
            width: '32',
            height: '32',
          },
        },
        {
          selector: 'node[group = "indicators"][type = "startHubIcon"].zoom_6',
          style: {
            width: '48',
            height: '48',
          },
        },
        {
          selector: 'node[group = "indicators"][type = "startHubIcon"].zoom_7',
          style: {
            width: '56',
            height: '56',
            display: 'element',
          },
        },
        {
          selector: 'node.editor:selected',
          style: {
            'border-color': () => this.getVar('--white'),
            'border-opacity': 1,
            'border-width': '3px',
          },
        },
        {
          selector: 'node.editor.selected:selected',
          style: {
            'border-opacity': 0,
          },
        },
      ],
      done: this.attachEvents.bind(this),
    });

    this.cssVariables = this.cy.cssVars({
      initialVars: Object.fromEntries(Object.entries(this.theme.colors).map(([key, value]) => [`--${key}`, value])),
      domEl: document.body,
    });
    this.cssVariables.update();

    this.cy.overlaps = (pos, size) => {
      let overlap = false;
      this.cy.nodes(':visible').forEach((node) => {
        const npos = node.renderedPosition();
        if (Math.hypot(pos.x - npos.x, pos.y - npos.y) <= size) {
          overlap = true;
          return false;
        }
      });
      return overlap;
    };

    if ($('#viewer').length) {
      this.cy
        .elements()
        .animate(
          {
            style: { opacity: 0 },
          },
          {
            duration: 0,
          },
        )
        .animate(
          {
            style: { opacity: 1 },
          },
          {
            duration: 1000,
            easing: 'ease-in-out',
          },
        )
        .animate({
          complete: () => this.cy.elements().style('opacity', 1),
        });
      this.cy.boxSelectionEnabled(false);
      this.cy.nodes().unselectify();
    } else {
      this.cy.elements().addClass('editor');
      this.cy.elements().style('opacity', 1);
      this.cy.boxSelectionEnabled(true);
      this.cy.nodes('node[placeholder]').unselectify();
    }
  }

  attachEvents(e) {
    e.cy.on('cyedgehandles.start', () => {
      this.draggingEdge = true;
    });

    e.cy.on('cyedgehandles.stop', () => {
      this.draggingEdge = false;
    });

    e.cy.on('add', 'node', (e) => {
      if (this.draggingEdge) {
        return;
      }
      this.options.onNodeAdded(e.target);
    });

    e.cy.on('position', 'node', (e) => {
      e.target.data('moved', true);
    });

    e.cy.on('free', '[^group]', (e) => {
      const target = e.target;
      if (!target.removed() && target.data('moved')) {
        const position = target.data('old_position');
        target.removeData('old_position');
        this.options.onNodeMoved(target, { old_position: position });
        target.removeData('moved');
      }
    });

    e.cy.on('grab', '[^group]', (e) => {
      const position = e.target.position();
      e.target.data('old_position', { x: position.x, y: position.y });
    });

    if (e.cy.edgehandles) {
      this.edgeHandle = e.cy.edgehandles({
        canConnect: function (source, target) {
          if (target.is('[!group][?initialized]')) {
            const gather = target.neighbourhood();
            const actual = gather.filter((el) => {
              return !el.hasClass('eh-preview-active');
            });

            return !(actual.anySame(source) || target.same(source));
          } else {
            return !target || target.size() === 0;
          }
        },
        edgeParams: this.options.edgeParams.bind(this),
      });

      e.cy.on('ehstart', () => {
        e.cy.scratch('edge-handle.active', true);
      });

      e.cy.on('ehstop', () => {
        e.cy.scratch('edge-handle.active', false);
      });

      e.cy.on('ehcancel', (ev, source) => {
        if (source.length === 1) {
          const position = ev.renderedPosition;
          while (this.cy.overlaps(position, source.width())) {
            position.x += 100;
            position.y += 100;
          }
          this.options.onNodeRequested(source, position);
        }
      });

      e.cy.on('ehcomplete', (event, sourceNode, targetNode, addedEdge) => {
        this.options.onEdgeAdded(sourceNode, targetNode, addedEdge);
      });
    }

    if (e.cy.tools) {
      e.cy.tools({
        fillStyle: this.theme.colors.hoverGrey,
        toolsIcon: this.options.iconPaths.toolsIcon,
        edgeIcons: {
          hidden: this.options.iconPaths.hiddenEdge,
          locked: this.options.iconPaths.lockedEdge,
          code: this.options.iconPaths.codeEdge,
          visible: this.options.iconPaths.visibleEdge,
        },
        exclusionSelector: 'node[placeholder]',
        edgeToolClicked: this.options.onEdgeToolClicked,
      });
    }
  }

  startEdge() {
    if (this.edgeHandle && this.sourceNode) {
      this.edgeHandle.start(this.sourceNode);
    }
  }

  stopEdge() {
    if (this.edgeHandle) {
      this.edgeHandle.stop();
    }
  }

  setHandleOn(node) {
    if (this.handleHover || this.handleNode) {
      return;
    }

    if (!this.options.isHubCreationAllowed || !this.options.isHubCreationAllowed()) {
      return;
    }

    this.handleNode = this.cy.add({
      classes: 'eh-handle',
      position: node.position(),
      grabbable: false,
      selectable: false,
    });

    this.sourceNode = node;
  }

  removeHandle() {
    if (this.handleHover) {
      return;
    }

    if (this.handleNode) {
      this.handleNode.remove();
      this.handleNode = null;
    }

    if (this.sourceNode) {
      this.sourceNode = null;
    }
  }
}
